import React, { useState } from 'react';

import {
  ActionIcon,
  Alert,
  Box,
  BoxProps,
  Button,
  ButtonProps,
  CopyButton,
  Flex,
  Modal,
  Table,
  Text,
  Title
} from '@mantine/core';
import { Prism } from '@mantine/prism';
import { IconAlertCircle, IconCheck, IconCopy, IconKey, IconPlus, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ErrorAlert } from 'components/ErrorAlert';
import { NoDataFound } from 'components/NoDataFound';
import { UpgradePlanBlurCard } from 'components/UpgradePlanBlurCard';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { useTranslation } from 'react-i18next';
import { ApiKeyService } from 'services/api-key-service';
import { NotificationService } from 'services/notification-service';
import { MAX_API_KEYS_PER_USER } from 'shared/constants/api-keys-constant';
import { API_KEYS_QUERY_KEY, ZAPIER_API_KEYS_QUERY_KEY } from 'shared/constants/query-keys';
import { ApiKey, ApiKeyWithUnHashedKey } from 'shared/interfaces/ApiKey';
import { formatDate } from 'shared/utils/format';

interface ApiKeysTableProps extends BoxProps {
  isZapierKey: boolean;
}

export function ApiKeysTable({ isZapierKey, ...boxProps }: ApiKeysTableProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [subscription] = useSubscriptionContext();
  const [newApiKey, setNewApiKey] = useState<string>('');
  const FEED_BACK_TRANSLATION_KEY = isZapierKey ? 'ZAPIER_API_KEY' : 'API_KEY';
  const TRANSLATION_KEY = isZapierKey ? 'ZAPIER' : 'API';

  const apiKeysQuery = useQuery({
    queryKey: [isZapierKey ? ZAPIER_API_KEYS_QUERY_KEY : API_KEYS_QUERY_KEY],
    queryFn: () => ApiKeyService.getAllApiKeys(isZapierKey),
    enabled:
      ((!isZapierKey && subscription.product.enableApiAccess) ||
        (isZapierKey && subscription.product.enableZapierAccess)) ??
      false
  });

  const createApiKey = useMutation({
    mutationFn: () => ApiKeyService.createApiKey(isZapierKey),
    onSuccess: (apiKeyWithUnHashedKey: ApiKeyWithUnHashedKey) => {
      const { unHashedKey, ...apiKeyWithoutUnHashedKey } = apiKeyWithUnHashedKey;
      setNewApiKey(unHashedKey);
      queryClient.setQueryData(
        [isZapierKey ? ZAPIER_API_KEYS_QUERY_KEY : API_KEYS_QUERY_KEY],
        (existingApiKeys: ApiKey[] = []) => [...existingApiKeys, apiKeyWithoutUnHashedKey]
      );
    },
    onError: () => NotificationService.showResult(false, `FEED_BACK.${FEED_BACK_TRANSLATION_KEY}.FAILURE.CREATE`)
  });

  const deleteApiKey = useMutation({
    mutationFn: ApiKeyService.deleteApiKey,
    onSuccess: (deletedApiKey: ApiKey) => {
      queryClient.setQueryData(
        [isZapierKey ? ZAPIER_API_KEYS_QUERY_KEY : API_KEYS_QUERY_KEY],
        (existingApiKeys: ApiKey[] = []) => existingApiKeys.filter((apiKey) => apiKey._id !== deletedApiKey._id)
      );
      NotificationService.showResult(true, `FEED_BACK.${FEED_BACK_TRANSLATION_KEY}.SUCCESS.DELETE`);
    },
    onError: () => NotificationService.showResult(false, `FEED_BACK.${FEED_BACK_TRANSLATION_KEY}.FAILURE.DELETE`)
  });

  if (apiKeysQuery.isError) return <ErrorAlert w="100%" h="100%" />;

  const shouldShowBlurCard = () => {
    if (isZapierKey) return !subscription.product.enableZapierAccess;
    return !subscription.product.enableApiAccess;
  };

  const CreateButton = ({ ...buttonProps }: ButtonProps) => (
    <Button
      loading={createApiKey.isLoading}
      disabled={(apiKeysQuery.data?.length ?? 0) >= MAX_API_KEYS_PER_USER}
      leftIcon={<IconPlus />}
      size="xs"
      onClick={() => createApiKey.mutate()}
      {...buttonProps}>
      {t('APP.ACTIONS.ADD')}
    </Button>
  );

  return (
    <>
      <UpgradePlanBlurCard message={t(`${TRANSLATION_KEY}.UPGRADE.DESCRIPTION`)} showCard={shouldShowBlurCard()}>
        <Box w="100%" h="100%" ml="auto" mr="auto" {...boxProps}>
          <Flex direction="row" align="flex-start" justify="space-between" w="100%" gap="xl">
            <Table h="100%">
              <thead>
                <tr>
                  <th>{t(`${TRANSLATION_KEY}.TABLE.KEY`)}</th>
                  <th>{t(`${TRANSLATION_KEY}.TABLE.LAST_USED_AT`)}</th>
                  <th>{t(`${TRANSLATION_KEY}.TABLE.CREATED_AT`)}</th>
                  <th>{t('APP.ACTIONS.DELETE')}</th>
                </tr>
              </thead>
              <tbody>
                {apiKeysQuery.data?.map((apiKey: ApiKey, key: number) => (
                  <tr key={key}>
                    <td>
                      <Text fw={500}>{apiKey.keyHint}</Text>
                    </td>
                    <td>{apiKey.lastUsedAt > 0 ? formatDate(apiKey.lastUsedAt) : '---'}</td>
                    <td>{formatDate(apiKey.createdAt)}</td>
                    <td>
                      <ActionIcon
                        onClick={() => deleteApiKey.mutate({ apiKeyId: apiKey._id, isZapierKey })}
                        color="red">
                        <IconTrash />
                      </ActionIcon>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {apiKeysQuery.data?.length == 0 ? undefined : <CreateButton variant="default" />}
          </Flex>

          {apiKeysQuery.data?.length == 0 && (
            <NoDataFound
              h="100%"
              size="xs"
              icon={<IconKey />}
              title={t(`NO_DATA_FOUND.${isZapierKey ? 'ZAPIER_API_KEYS' : 'API_KEYS'}.TITLE`)}
              subTitle={t(`NO_DATA_FOUND.${isZapierKey ? 'ZAPIER_API_KEYS' : 'API_KEYS'}.SUB_TITLE`)}
              button={<CreateButton size="xs" />}
            />
          )}
          {(apiKeysQuery.data?.length ?? 0) >= MAX_API_KEYS_PER_USER && (
            <Text align="center" color="gray.5">
              {t(`${TRANSLATION_KEY}.MAX_API_KEYS`, { maxApiKeys: MAX_API_KEYS_PER_USER })}
            </Text>
          )}
        </Box>
      </UpgradePlanBlurCard>
      <Modal
        opened={!!newApiKey}
        withCloseButton={true}
        title={
          <Title order={3} mb={0}>
            {t(`${TRANSLATION_KEY}.MODAL.TITLE`)}
          </Title>
        }
        onClose={() => setNewApiKey('')}>
        <Alert
          mt={-10}
          mb={20}
          icon={<IconAlertCircle size={16} />}
          title={t(`${TRANSLATION_KEY}.MODAL.IMPORTANT`)}
          color="yellow">
          <Text>{t(`${TRANSLATION_KEY}.MODAL.DESCRIPTION`)}</Text>
        </Alert>
        <Flex
          direction="row"
          align="center"
          justify="space-between"
          w="100%"
          sx={(theme) => ({ borderRadius: '10px', backgroundColor: `${theme.colors.gray[1]}` })}>
          <Prism
            language="jsx"
            noCopy
            sx={(theme) => ({
              borderRadius: '10px',
              width: '88%',
              '.mantine-Prism-code': {
                backgroundColor: `${theme.colors.gray[1]} !important`
              }
            })}>
            {newApiKey}
          </Prism>
          <CopyButton value={newApiKey} timeout={2000}>
            {({ copied, copy }) => (
              <ActionIcon mr={7} onClick={() => copy()}>
                {copied ? <IconCheck size={22} /> : <IconCopy size={22} />}
              </ActionIcon>
            )}
          </CopyButton>
        </Flex>
      </Modal>
    </>
  );
}
